import { motion } from "framer-motion";
import { useState } from "react";
import { useData } from "../context/DataContext";
import Breadcrumb from "../components/Breadcrumb";
import ControlActions from "../components/ControlActions";
import { Code2, History, Download } from "lucide-react";

// Brand Icons Helper - File Based
// Brand Icons Helper - File Based
const TechIcon = ({ url }: { url: string }) => {
    const [error, setError] = useState(false);

    if (error) {
        return null; // Don't show broken icons
    }

    return (
        <img
            src={url}
            alt="tech-icon"
            className="w-10 h-10 object-contain hover:scale-110 transition-transform duration-300 opacity-60 hover:opacity-100 grayscale hover:grayscale-0"
            onError={() => setError(true)}
        />
    );
};

const TechIconMarquee = () => {
    const [icons, setIcons] = useState<string[]>([]);

    useState(() => {
        const fetchIcons = async () => {
            try {
                const apiUrl = process.env.API_URL || '';
                const res = await fetch(`${apiUrl}/api/tech-icons`);
                if (res.ok) {
                    const paths = await res.json();
                    // Append API URL to paths
                    setIcons(paths.map((p: string) => `${apiUrl}${p}`));
                }
            } catch (e) {
                console.error("Failed to fetch icons", e);
            }
        };
        fetchIcons();
    });

    if (icons.length === 0) return null;

    // Triple the list for smooth infinite scroll
    const marqueeList = [...icons, ...icons, ...icons];

    return (
        <motion.div
            className="flex gap-12 w-max pl-4 items-center"
            animate={{ x: ["0%", "-33.33%"] }}
            transition={{ ease: "linear", duration: 25, repeat: Infinity }}
        >
            {marqueeList.map((url, i) => (
                <div key={i} className="flex items-center justify-center group relative w-12 h-12 shrink-0">
                    <TechIcon url={url} />
                </div>
            ))}
        </motion.div>
    );
};

export default function About() {
    const radiusClass = "rounded-[20px]";
    const borderClass = "border-2 border-[#2A2A2A] hover:border-[#404040]";

    const { data: globalData } = useData();
    const data = globalData.about;

    const containerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.04,
                delayChildren: 0
            }
        }
    };

    const itemVariants: any = {
        hidden: { opacity: 0, y: 20, scale: 0.98 },
        show: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: { duration: 0.4, ease: "backOut" }
        }
    };

    // Loading check inside JSX
    // if (!data) return null;

    // Duplicate skills for infinite marquee (Guard against null data)
    const marqueeSkills = data ? [...data.skills.list, ...data.skills.list, ...data.skills.list] : [];

    return (
        <motion.div
            initial="hidden"
            animate="show"
            exit={{ opacity: 0 }}
            variants={containerVariants}
            className="min-h-screen flex flex-col items-center py-24 px-4 md:py-16 pb-32"
        >
            {!data ? (
                // SKELETON MESH
                <div className="w-full max-w-4xl mx-auto h-auto flex flex-col gap-6 animate-pulse">
                    {/* Control Row Skeleton */}
                    <div className="flex w-full gap-4 h-[54px]">
                        <div className="flex-grow bg-[#111] rounded-[20px] border border-[#222]" />
                        <div className="w-32 bg-[#111] rounded-[20px] border border-[#222]" />
                    </div>

                    {/* Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
                        {/* Left Col */}
                        <div className="col-span-1 md:col-span-2 flex flex-col gap-6">
                            {/* Bio Skeleton */}
                            <div className="min-h-[300px] flex-grow bg-[#111] rounded-[20px] border border-[#222] p-10 flex flex-col gap-6">
                                <div className="w-32 h-6 bg-[#222] rounded-full opacity-40" />
                                <div className="space-y-3">
                                    <div className="w-full h-4 bg-[#222] rounded opacity-20" />
                                    <div className="w-full h-4 bg-[#222] rounded opacity-20" />
                                    <div className="w-3/4 h-4 bg-[#222] rounded opacity-20" />
                                </div>
                            </div>
                            {/* Marquee Skeleton */}
                            <div className="h-[100px] bg-[#111] rounded-[20px] border border-[#222]" />
                        </div>

                        {/* Right Col */}
                        <div className="col-span-1 bg-[#111] rounded-[20px] border border-[#222] min-h-[500px] p-6 flex flex-col gap-6">
                            <div className="w-40 h-6 bg-[#222] rounded-full opacity-40" />
                            {/* Timeline items */}
                            {[1, 2, 3].map(i => (
                                <div key={i} className="h-24 bg-[#1a1a1a] rounded-xl border border-[#222]" />
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="w-full max-w-4xl mx-auto h-auto flex flex-col gap-6">

                    {/* 2. CONTROL ROW */}
                    <motion.div variants={itemVariants} style={{ willChange: "transform, opacity" }} className="flex flex-row gap-4">
                        <div className={`flex-grow bg-[var(--card-bg)] ${radiusClass} ${borderClass} px-8 flex items-center h-[54px] shadow-sm`}>
                            <Breadcrumb items={[{ label: "About" }]} />
                        </div>
                        <ControlActions title={data.header.title} />
                    </motion.div>

                    {/* 3. MAIN CONTENT GRID - Equal Height Layout */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">

                        {/* LEFT COLUMN: BIO (Flex Grow) + TECH STACK */}
                        <div className="col-span-1 md:col-span-2 flex flex-col gap-6 h-full">

                            {/* BIO CARD (Refined Clean Design) */}
                            <motion.div
                                variants={itemVariants}
                                style={{ willChange: "transform, opacity" }}
                                className={`bg-[var(--card-bg)] ${radiusClass} ${borderClass} p-8 md:p-10 relative overflow-hidden flex-grow flex flex-col`}
                            >
                                <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--accent)]/5 rounded-full blur-[80px] -mr-16 -mt-16 pointer-events-none" />

                                <div className="relative z-10">
                                    <h3 className="text-white font-bold text-lg mb-6 flex items-center gap-3">
                                        <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full shadow-[0_0_10px_var(--accent)]" />
                                        {data.bio.title}
                                    </h3>
                                    <div className="prose prose-invert max-w-none">
                                        <p className="text-[#bbb] text-base md:text-lg leading-[1.8] font-normal whitespace-pre-wrap">
                                            {data.bio.content}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>

                            {/* TECH STACK CARD (Pure Logo Bar) */}
                            <motion.div
                                variants={itemVariants}
                                style={{ willChange: "transform, opacity" }}
                                className={`bg-[var(--card-bg)] ${radiusClass} ${borderClass} h-[100px] overflow-hidden relative flex flex-col justify-center shrink-0 py-0`}
                            >
                                <div className="relative w-full overflow-hidden mask-linear-fade">
                                    <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-[var(--card-bg)] to-transparent z-20 pointer-events-none" />
                                    <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[var(--card-bg)] to-transparent z-20 pointer-events-none" />

                                    <TechIconMarquee />
                                </div>
                            </motion.div>

                        </div>

                        {/* RIGHT COLUMN: TIMELINE (Stretches to Match) */}
                        <motion.div
                            variants={itemVariants}
                            style={{ willChange: "transform, opacity" }}
                            className={`col-span-1 bg-[#111] ${radiusClass} ${borderClass} p-6 md:p-6 flex flex-col h-full`}
                        >
                            <h3 className="text-white font-bold text-lg mb-6 flex items-center gap-3 px-2">
                                <div className="p-2 rounded-lg bg-[#222] border border-[#333] text-[var(--accent)]">
                                    <History size={18} />
                                </div>
                                {data.timeline.title}
                            </h3>

                            {/* Stacked Experience Cards */}
                            <div className="flex flex-col gap-4 mb-6 flex-grow">
                                {data.timeline.items.map((item, i) => (
                                    <div key={i} className="bg-[#1a1a1a] rounded-xl p-5 border border-[#222] hover:border-[var(--accent)] transition-colors group relative overflow-hidden">
                                        <div className="flex justify-between items-start mb-2 relative z-10">
                                            <h4 className="text-white text-sm font-bold leading-tight group-hover:text-[var(--accent)] transition-colors">{item.title}</h4>
                                            <span className="text-[var(--accent)] font-mono text-[10px] font-bold bg-[var(--accent)]/10 px-2 py-0.5 rounded ml-2 whitespace-nowrap">
                                                {item.year}
                                            </span>
                                        </div>
                                        <p className="text-[12px] text-[#666] leading-relaxed group-hover:text-[#999] transition-colors relative z-10">
                                            {item.desc}
                                        </p>
                                    </div>
                                ))}
                            </div>

                            {/* Download CV Button */}
                            <div className="mt-auto pt-4 border-t border-[#222]">
                                <a
                                    href="/resume.pdf"
                                    target="_blank"
                                    className={`w-full h-[50px] rounded-[16px] bg-transparent border-2 border-[#333] text-[#888] font-bold text-sm flex items-center justify-center gap-2 hover:border-[var(--accent)] hover:text-white hover:bg-[var(--accent)]/5 transition-all duration-300 group`}
                                >
                                    <Download size={18} className="text-[#666] group-hover:text-[var(--accent)] transition-colors" />
                                    <span>CV İndir</span>
                                </a>
                            </div>
                        </motion.div>

                    </div>
                </div>
            )}
        </motion.div>
    );
}
